import _, {chain, sortBy} from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {LensModel} from '../../../../models/api/lensModel';
import {VisualWorkspaceLens} from '../../../../models/api/visualWorkspaceModel';
import {AppState} from '../../../../store/appState';
import {getLenses} from '../../../../store/data/lenses/lensesSelectors';
import {WorkspaceDropReorderLensHandler} from '../../../../store/data/workspace/reorder/types';
import {
  computeVisualWorkspaceRecentlyViewedMap,
  VisualWorkspaceRecentlyViewedMap,
} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {WorkspaceDragSourceTypesEnum} from '../dnd/workspaceDragDropTypes';
import {lensesWorkspaceSortComparator} from '../lensesWorkspace/lensesWorkspaceHelpers';
import {LensesWorkspaceItemContainerBase} from '../lensesWorkspace/lensesWorkspaceItemContainerBase';
import {ToggleLensHandler} from './context/workspacesEditorContext';

/*
 * Props.
 */

interface WorkspacesEditorLensesProps {
  isAlphabetized: boolean;
  onToggleLens: ToggleLensHandler;
  panelId: InboxesPanelId;
  onDropReorderLens: WorkspaceDropReorderLensHandler;
}

interface WorkspacesEditorLensContainerComponentProps extends WorkspacesEditorLensesProps {
  recentlyViewedMap: VisualWorkspaceRecentlyViewedMap;
  selectedLensIds: ReadonlyArray<number>;
  lenses: ReadonlyArray<LensModel>;
}

interface WorkspacesEditorLensContainerProps extends WorkspacesEditorLensesProps {
  visualWorkspaceLenses: ReadonlyArray<VisualWorkspaceLens>;
}

/*
 * Selectors.
 */

function getVisualWorkspaceLenses(_state: AppState, props: WorkspacesEditorLensContainerProps) {
  return props.visualWorkspaceLenses;
}

function getSelectedLensIds(_state: AppState, props: WorkspacesEditorLensContainerProps) {
  return _(props.visualWorkspaceLenses)
    .filter((lens) => lens.isSelected)
    .map((lens) => lens.id)
    .value();
}

const getRecentlyViewedMap = createSelector([getVisualWorkspaceLenses], (visualWorkspaceLenses) =>
  computeVisualWorkspaceRecentlyViewedMap(visualWorkspaceLenses),
);

const getWorkspaceLenses = createSelector(
  [getVisualWorkspaceLenses, getLenses],
  (visualWorkspaceLenses, lenses) =>
    chain(visualWorkspaceLenses)
      .map(({id}) => lenses[id])
      .compact()
      .value(),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<WorkspacesEditorLensContainerProps>()(() => ({
  recentlyViewedMap: getRecentlyViewedMap,
  selectedLensIds: getSelectedLensIds,
  lenses: getWorkspaceLenses,
}));

const WorkspacesEditorLensesContainerComponent: FC<WorkspacesEditorLensContainerComponentProps> = (props) => {
  const {
    panelId,
    lenses,
    isAlphabetized,
    recentlyViewedMap,
    onDropReorderLens,
    selectedLensIds,
    onToggleLens,
  } = props;
  const sortedLenses = isAlphabetized ? sortBy(lenses, lensesWorkspaceSortComparator) : lenses;
  const lastIndex = sortedLenses.length - 1;
  return (
    <>
      {sortedLenses.map((t, index) => (
        <LensesWorkspaceItemContainerBase
          key={t.id}
          panelId={panelId}
          lens={t}
          isRecentlyViewed={recentlyViewedMap[t.id]}
          isChecked={selectedLensIds.includes(t.id)}
          onClick={() => {
            onToggleLens(t.id, false);
          }}
          shouldHideMoreMenu
          isEditingWorkspace
          shouldAlphabetizeWorkspace={isAlphabetized}
          shouldShowRefinedResultsIndicator={false}
          workspaceDragItem={{
            type: WorkspaceDragSourceTypesEnum.LENS,
            id: t.id,
            isLast: index === lastIndex,
          }}
          onDropReorderLens={onDropReorderLens}
        />
      ))}
    </>
  );
};

export const WorkspacesEditorLensesContainer = connect(mapStateToProps)(
  WorkspacesEditorLensesContainerComponent,
);
